/**
 * paragraph-reveal (Phase 4-2, Task 2.1) segment reveal hook。
 *
 * StreamingSceneView 既存の visibleCount/auto-timer/click-advance ロジックを
 * 共通 hook として抽出。SceneView (sync 経路) と StreamingSceneView (streaming 経路) の
 * 両方から呼び出すことで、scene 1 でも segment 単位 reveal を実現する。
 *
 * 主な責務:
 *   - autoEnabled=true + paused=false のとき setTimeout で auto 進行 (50ms/字、最低 300ms)
 *   - autoEnabled=false で副作用ゼロ (= 全 segment 即時表示、bit-by-bit 旧挙動互換)
 *   - prefers-reduced-motion: reduce 検出時は autoEnabled の値に関わらず即時全表示
 *   - segments 配列の `0 → 非空` 変化時のみ visibleCount=1 にリセット (streaming 受信中の追加では維持)
 *
 * 設計詳細は `.kiro/specs/paragraph-reveal/design.md` §Service Interface §useSegmentReveal を参照。
 */

import { useCallback, useEffect, useState } from "react";
import type { components } from "../../api/schema";
import {
  PACING_MULTIPLIER,
  type ReaderPacing,
} from "../reader-style/types";

type Segment = components["schemas"]["Segment"];

export interface UseSegmentRevealOptions {
  /** 表示対象 segment 配列。新 scene 受信で 0→非空に変わると visibleCount=1 にリセットする。 */
  segments: readonly Segment[];
  /** 一時停止 (worldline-palimpsest モーダル表示中など)。auto 停止 + advance no-op。 */
  paused?: boolean;
  /**
   * auto 進行を有効にするか。呼出側が以下を合成して渡す:
   *   - SceneView: env.paragraphRevealEnabled && !readOnly
   *   - StreamingSceneView: env.paragraphRevealEnabled
   * false のとき timer を一切張らず、visibleCount は segments.length 即値 (= 全表示)。
   */
  autoEnabled: boolean;
  /**
   * click/tap のみで進める手動 reveal。visual_novel mode で使う。
   * autoEnabled=false の既存「全表示」互換を壊さないため、別フラグとして扱う。
   */
  manualEnabled?: boolean;
  /**
   * reader-style-preset (= 副次 spec) δ-1 採択:
   * reader が選択した読み進めの速さ (= "slow" | "normal" | "fast")。
   * 未指定 (= 既存 callsite backward compat) で "normal" 採用 (= 50ms/字 base そのまま)。
   * "slow" = ×1.5、 "fast" = ×0.5、 "normal" = ×1.0。
   */
  pacing?: ReaderPacing;
}

export interface UseSegmentRevealResult {
  /** 現在表示すべき segment 数 (0 ≤ visibleCount ≤ segments.length)。 */
  visibleCount: number;
  /** click/tap で次 segment へ進める callback。paused / allVisible で no-op。 */
  advance: () => void;
  /** visibleCount >= segments.length の真偽 (choices/advance UI 表示条件に使う)。 */
  allVisible: boolean;
}

// セグメントのテキスト長に基づいて autoAdvance の遅延時間 (ms) を算出する。
// 1 文字あたり 50ms × PACING_MULTIPLIER を基準とし、最低でも 300ms は確保する。
// reader-style-preset (= 副次 spec) δ-1 採択で pacing 切替対応、 既存挙動は "normal" で互換。
function calcAutoAdvanceMs(segment: Segment, pacing: ReaderPacing): number {
  const charCount = segment.text.length;
  return Math.max(charCount * 50 * PACING_MULTIPLIER[pacing], 300);
}

function readReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * `prefers-reduced-motion: reduce` の現在値を React state として取得する hook。
 * SSR / matchMedia 未対応環境では false を返す。
 * media query の動的変更 (OS 設定変更など) には change event listener で追従する。
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => readReducedMotion());

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent): void => {
      setReduced(e.matches);
    };
    mq.addEventListener("change", handler);
    return () => {
      mq.removeEventListener("change", handler);
    };
  }, []);

  return reduced;
}

export function useSegmentReveal(
  opts: UseSegmentRevealOptions,
): UseSegmentRevealResult {
  const {
    segments,
    paused = false,
    autoEnabled,
    manualEnabled = false,
    pacing = "normal",
  } = opts;
  const prefersReducedMotion = useReducedMotion();
  const revealMode: "auto" | "manual" | "all" = manualEnabled
    ? "manual"
    : autoEnabled && !prefersReducedMotion
      ? "auto"
      : "all";

  // 表示中のセグメント数。
  //   - all:    segments.length 即値 (全表示)
  //   - auto:   segments が 1 件以上あれば 1、なければ 0
  //   - manual: auto と同じ初期表示だが timer は張らず click/tap だけで進む
  const [visibleCount, setVisibleCount] = useState<number>(() =>
    revealMode === "all" ? segments.length : Math.min(1, segments.length),
  );

  // 全表示 mode のとき visibleCount を segments.length に固定する。
  // motion-reduce が true に切り替わった場合や autoEnabled が false に切り替わった場合の追従を担う。
  useEffect(() => {
    if (revealMode === "all") {
      setVisibleCount(segments.length);
    }
  }, [revealMode, segments.length]);

  // segments が空→非空に変化したタイミングで visibleCount を 1 にリセットする
  // (新 scene 受信時の挙動、StreamingSceneView 既存実装を踏襲)。
  // 同じ非空状態で segment が追加されただけ (streaming 受信) では reset しない。
  // all mode のときは上の effect が segments.length 固定を維持する。
  useEffect(() => {
    if (revealMode === "all") return;
    setVisibleCount(Math.min(1, segments.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments.length === 0 ? 0 : 1, revealMode]);
  // 上記の依存配列は意図的: segments が空→非空になった瞬間のみリセットしたいため

  // まだ表示していないセグメントがある場合は autoAdvance タイマーを設定する。
  // manual/all mode / paused のときはタイマーを張らない。
  useEffect(() => {
    if (revealMode !== "auto") return;
    if (paused) return;
    if (visibleCount >= segments.length) return;

    const currentSegment = segments[visibleCount - 1];
    if (!currentSegment) return;

    const delay = calcAutoAdvanceMs(currentSegment, pacing);
    const timerId = setTimeout(() => {
      setVisibleCount((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timerId);
  }, [visibleCount, segments, paused, revealMode, pacing]);

  const allVisible = visibleCount >= segments.length;

  // click/tap で次 segment へ進める。paused / allVisible で no-op。
  const advance = useCallback(() => {
    if (paused) return;
    setVisibleCount((prev) => (prev < segments.length ? prev + 1 : prev));
  }, [paused, segments.length]);

  return { visibleCount, advance, allVisible };
}
